import { Injectable } from '@angular/core';
import { storageKeys } from 'src/app/share/configs';
import { WindowService } from './window.service';

export interface ExternalCheckoutContext {
  externalId: string;
  platformType: number;
  sParam?: string | null;
  storeId?: number;
  userId?: number;
  updatedAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ExternalCheckoutContextService {
  private readonly aiPlatformType = 1;
  private readonly ttlMs = 2 * 60 * 60 * 1000;

  constructor(private windowServe: WindowService) {}

  saveContext(
    context: Omit<ExternalCheckoutContext, 'updatedAt'> &
      Partial<Pick<ExternalCheckoutContext, 'updatedAt'>>
  ): void {
    const value: ExternalCheckoutContext = {
      ...context,
      updatedAt: Date.now(),
    };

    this.windowServe.setSessionStorage(
      storageKeys.externalCheckoutContext,
      JSON.stringify(value)
    );
  }

  getContext(): ExternalCheckoutContext | null {
    const raw = this.windowServe.getSessionStorage(
      storageKeys.externalCheckoutContext
    );
    if (!raw || typeof raw !== 'string') {
      return null;
    }

    try {
      const context = JSON.parse(raw) as ExternalCheckoutContext;
      if (
        !context.externalId ||
        context.platformType === undefined ||
        context.platformType === null
      ) {
        this.clearContext();
        return null;
      }

      if (Date.now() - context.updatedAt > this.ttlMs) {
        this.clearContext();
        return null;
      }

      const currentStoreId = this.windowServe.getLocalStorage(storageKeys.store);
      const currentUserId = this.windowServe.getLocalStorage(storageKeys.user);

      if (
        context.storeId &&
        currentStoreId &&
        context.storeId.toString() !== currentStoreId
      ) {
        this.clearContext();
        return null;
      }

      if (
        context.userId &&
        currentUserId &&
        context.userId.toString() !== currentUserId
      ) {
        this.clearContext();
        return null;
      }

      return context;
    } catch (error) {
      this.clearContext();
      return null;
    }
  }

  getPlatformType(): number | null {
    return this.getContext()?.platformType ?? null;
  }

  isAiExternalCheckout(): boolean {
    return this.getPlatformType() === this.aiPlatformType;
  }

  clearContext(): void {
    this.windowServe.removeSessionStorage(storageKeys.externalCheckoutContext);
  }

  withPlatformType<T extends Record<string, any>>(payload: T): T {
    const platformType = this.getPlatformType();
    if (platformType === null) {
      return payload;
    }

    return {
      ...payload,
      platform_type: platformType,
    };
  }
}
